import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import { withRouter } from 'react-router-dom';
import { keyBy, take, toArray } from 'lodash';

import { STATIC_ROOT, HEADER_DESKTOP_MEDIA_QUERY } from '../../../constants';
import { RECHERCHE_VERSION_MENU_MOBILE_SLIDE } from '../../../services/header/constants';

import { toggleMenuMobileSlide } from '../../../services/header/actions';
import { searchInput, searchClear } from '../../../services/search/actions';

import { getMenuVersion, isSpecificMobileNavOpen } from '../../../services/header/selector';

import {
    hasCashback,
    hasDisplayableCoupon,
    hasOffline,
    hasCashbackOffline,
    hasEbonCashback
} from '../../../services/partner/utils';
import { searchPartners } from '../../../services/search/utils';
import { isBilletterie, toBilletteriePartners } from '../../../services/billetterie/utils';

import SvgComponent from '../../SvgComponent';
import { ResultComponent } from './ResultComponent';
import ButtonComponent from '../../button/ButtonComponent';

import './SearchComponent.scss';

const MAX_RESULTS = 12;

class RechercheComponent extends React.Component {
    state = {
        showList: false,
        inputValue: '',
        number: -1,
        isLabelFocused: false,
        isResultFocused: false
    };

    componentDidMount() {
        document.body.addEventListener('click', this.onClickOutside);
        document.addEventListener('keydown', this.navigateSearchBar, false);
    }

    componentDidUpdate(prevProps) {
        const { pathname, isMenuMobileSearchOpen, menuMobileVersion } = this.props;

        if (pathname !== prevProps.pathname) {
            this.setState({ inputValue: '', showList: false, number: 0 });
            // Replie la barre de recherche étendue : à la navigation, le lien
            // de résultat focusé peut être démonté sans émettre de blur (le
            // header reste monté entre deux pages de même template).
            if (this.props.onSearchBlur) {
                this.props.onSearchBlur();
            }
        }
        if (
            !prevProps.isMenuMobileSearchOpen &&
            isMenuMobileSearchOpen &&
            menuMobileVersion === RECHERCHE_VERSION_MENU_MOBILE_SLIDE
        ) {
            document.addEventListener('keydown', this.trapFocus, false);
            document.querySelector('#search-menu-mobile-bt').focus();
        }
        if (
            prevProps.menuMobileVersion === RECHERCHE_VERSION_MENU_MOBILE_SLIDE &&
            prevProps.isMenuMobileSearchOpen &&
            !isMenuMobileSearchOpen
        ) {
            document.removeEventListener('keydown', this.trapFocus, false);
            document.querySelector('#champ-recherche-label-mobile-bt').focus();
        }
    }

    componentWillUnmount() {
        document.body.removeEventListener('click', this.onClickOutside);
        document.removeEventListener('keydown', this.navigateSearchBar, false);
        document.removeEventListener('keydown', this.trapFocus, false);
    }

    trapFocus = e => {
        const focusableElements = 'button, input, [tabindex]:not([tabindex="-1"])';
        const modal = document.querySelector('#recherche'); // select the modal by it's id

        const firstFocusableElement = modal.querySelectorAll(focusableElements)[0]; // get first element to be focused inside modal
        const focusableContent = modal.querySelectorAll(focusableElements);
        const lastFocusableElement = focusableContent[focusableContent.length - 1]; // get last element to be focused inside modal
        let isTabPressed = e.key === 'Tab';

        if (!isTabPressed) {
            return;
        }
        if (e.shiftKey) {
            // if shift key pressed for shift + tab combination
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus(); // add focus for the last focusable element
                e.preventDefault();
            }
        } else {
            // if tab key is pressed
            if (document.activeElement === lastFocusableElement) {
                // if focused has reached to last focusable element then focus first focusable element after pressing tab
                firstFocusableElement.focus(); // add focus for the first focusable element
                e.preventDefault();
            }
        }
    };

    navigateSearchBar = event => {
        const ul = document.getElementById('recherche-resultats');
        if (ul !== null) {
            // escape or tab
            if (event.keyCode === 27 || event.keyCode === 9) {
                this.setState({ showList: false });
            }

            const li = document.querySelectorAll('li.resultat');

            // arrow up
            if (event.keyCode === 38) {
                event.preventDefault();
                const newIndex =
                    this.state.number <= 0 || this.state.number > li.length - 1
                        ? li.length - 1
                        : this.state.number - 1;
                this.setState({ number: newIndex });
                li[newIndex].firstChild.focus();
            }

            // arrow down
            else if (event.keyCode === 40) {
                event.preventDefault();
                const newIndex = this.state.number < li.length - 1 ? this.state.number + 1 : 0;
                this.setState({ number: newIndex });
                li[newIndex].firstChild.focus();
            }

            // enter
            if (this.state.isResultFocused && event.keyCode === 13) {
                event.preventDefault();
                li[this.state.number].firstChild.click();
            }
            if (this.state.isLabelFocused && event.keyCode === 13) {
                event.preventDefault();
                this.setState({ number: 0 });
                li[0].firstChild.focus();
            }
        } else {
            const isInsideSearch = this._wrapper && this._wrapper.contains(document.activeElement);
            const isTab = event.keyCode === 9;
            if (isInsideSearch && !isTab && this.state.inputValue !== '') {
                this.setState({ showList: true, number: -1 });
                if (this.state.isLabelFocused && event.keyCode === 13) {
                    event.preventDefault();
                    const li = document.querySelectorAll('li.resultat');
                    li[0].firstChild.focus();
                }
            }
        }
    };

    onSearchFocus = event => {
        const ul = document.getElementById('recherche-resultats');
        if (ul !== null) {
            event.preventDefault();
            const li = document.querySelectorAll('li.resultat');
            this.setState({ number: 0 });
            li[0].firstChild.focus();
        }
    };

    onSearchClick = () => {
        const ul = document.getElementById('recherche-resultats');
        if (ul !== null) {
            const li = document.querySelectorAll('li.resultat');
            this.setState({ number: 0 });
            li[0].firstChild.focus();
        }
    };

    onInputEnter = event => {
        const ul = document.getElementById('recherche-resultats');
        if (ul !== null && event.keyCode === 13) {
            event.preventDefault();
            const li = document.querySelectorAll('li.resultat');
            this.setState({ number: 0 });
            li[0].firstChild.focus();
        }
    };

    onClickOutside = event => {
        const container = ReactDOM.findDOMNode(this._wrapper);
        if (container && !container.contains(event.target)) {
            this.setState({ showList: false, number: -1 });
        }
    };

    // Ré-affiche les résultats quand on revient sur le champ avec une saisie
    // encore présente (la liste a pu être fermée par un clic en dehors).
    onInputFocus = () => {
        if (this.state.inputValue.length > 0 && !this.state.showList) {
            this.setState({ showList: true });
        }
    };

    onClickBtMenuMobile = e => {
        e.preventDefault();
        this.props.toggleMenuMobileSlide(RECHERCHE_VERSION_MENU_MOBILE_SLIDE);
    };

    // Ferme la slide au clic sur l'overlay (la zone sombre hors du panneau).
    // Le test isMenuMobileSearchOpen évite tout effet sur la recherche inline
    // du header desktop, qui utilise le même <form>.
    onClickOverlay = e => {
        if (e.target === e.currentTarget && this.props.isMenuMobileSearchOpen) {
            this.props.toggleMenuMobileSlide(RECHERCHE_VERSION_MENU_MOBILE_SLIDE);
        }
    };

    // Prévient le header (bouton "Toutes les catégories" compact) quand le
    // focus entre ou sort du bloc recherche. Tant que le focus circule à
    // l'intérieur (input, suggestions), on ne signale pas de sortie.
    onContainerFocus = () => {
        if (this.props.onSearchFocus) {
            this.props.onSearchFocus();
        }
    };

    onContainerBlur = e => {
        if (this.props.onSearchBlur && !e.currentTarget.contains(e.relatedTarget)) {
            this.props.onSearchBlur();
        }
    };

    onChangeInput = event => {
        const inputValue = event.target.value;
        this.setState({ inputValue, showList: inputValue.length > 0 });
    };

    // Un clic dans le vide de la liste de résultats (hors lien) ne doit pas
    // replier la barre : on empêche le mousedown de voler le focus de l'input
    // (pas de blur). Le clic sur un lien navigue sans avoir besoin du focus.
    onResultsMouseDown = e => {
        e.preventDefault();
    };

    // Réinitialise la recherche au clic sur un résultat : si la fiche visée
    // est la page courante, le pathname ne change pas et la réinitialisation
    // de componentDidUpdate ne se déclenche pas (saisie comprise). Le lien
    // focusé étant démonté sans émettre de blur, la barre est aussi repliée.
    closeList = p => e => {
        this.setState({ inputValue: '', showList: false, number: 0 });
        if (this.props.onSearchBlur) {
            this.props.onSearchBlur();
        }
    };

    submitHandler(e) {
        e.preventDefault();
    }

    onLabelFocus = () => this.setState({ isLabelFocused: true });
    onLabelBlur = () => this.setState({ isLabelFocused: false });

    onResultFocus = () => this.setState({ isResultFocused: true });
    onResultBlur = () => this.setState({ isResultFocused: false });

    render() {
        const {
            i18n,
            isMenuMobileSearchOpen,
            partners,
            ebonPartners,
            billetterieVenues,
            i18nAccessibility
        } = this.props;
        const { isLabelFocused } = this.state;

        const value = this.form ? this.form.elements.champrecherche.value : '';

        const billetteriePartners = !!value
            ? toBilletteriePartners(billetterieVenues, partners, ebonPartners)
            : [];
        const billetteriePartnersById = keyBy(billetteriePartners, 'id');

        const results = !!value
            ? take(
                  searchPartners(
                      toArray(partners)
                          .filter(
                              p =>
                                  hasCashback(p) ||
                                  hasDisplayableCoupon(p) ||
                                  (hasOffline(p) && hasCashbackOffline(p))
                          )
                          .concat(
                              // Un marchand billetterie mène à sa fiche billetterie, pas bon d'achat
                              toArray(ebonPartners).filter(
                                  p => hasEbonCashback(p) && !billetteriePartnersById[p.id]
                              )
                          )
                          .concat(billetteriePartners),
                      value
                  ),
                  MAX_RESULTS
              )
            : [];

        return (
            <div
                className="recherche-container"
                role="search"
                onFocus={this.onContainerFocus}
                onBlur={this.onContainerBlur}
                ref={elt => {
                    this._wrapper = elt;
                }}>
                <button
                    type="button"
                    className="champ-recherche-label-mobile champ-recherche-label show-mobonly curpt"
                    id="champ-recherche-label-mobile-bt"
                    onClick={this.onClickBtMenuMobile}>
                    <SvgComponent
                        src={`${STATIC_ROOT}/picto-rechercher.svg`}
                        width="18"
                        height="18"
                        aria-hidden="true"
                        focusable="false"
                    />
                    <span className="sr-only">{i18nAccessibility.SEARCH_MENU_BUTTON_OPEN}</span>
                </button>

                <form
                    ref={elt => {
                        this.form = elt;
                    }}
                    onSubmit={this.submitHandler}
                    id="recherche"
                    className={`recherche menu-mobile-slide ${
                        isMenuMobileSearchOpen ? 'open' : 'closed'
                    }`}
                    role="dialog"
                    aria-label={i18nAccessibility.MODAL_LABEL_SEARCH}
                    aria-modal="true"
                    onClick={this.onClickOverlay}>
                    <div className="menu-mobile-slide-conteneur posrel">
                        <div className="show-mobonly">
                            <div className="bold flex-container-h flex-center-alt menu-mobile-slide-titre">
                                <div className="champ-recherche-label">
                                    <SvgComponent
                                        src={`${STATIC_ROOT}/picto-rechercher.svg`}
                                        width="18"
                                        height="18"
                                        aria-hidden="true"
                                        focusable="false"
                                    />
                                </div>
                                {i18n.FINDMERCHANT}
                                <button
                                    id="search-menu-mobile-bt"
                                    className={`menu-mobile-bt menu-mobile-bt-listener show-mobonly ${
                                        isMenuMobileSearchOpen ? 'open' : 'closed'
                                    }`}
                                    data-target="menu"
                                    onClick={this.onClickBtMenuMobile.bind(this)}>
                                    <span />
                                    <span className="sr-only">
                                        {i18nAccessibility.SEARCH_MENU_BUTTON_CLOSE}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div
                            className={`champ-recherche flex-container-h flex-center-alt input ${
                                isLabelFocused ? 'unfocus' : ''
                            }`}>
                            <MediaQuery query={HEADER_DESKTOP_MEDIA_QUERY}>
                                {matches => (
                                    <React.Fragment>
                                        <input
                                            type="search"
                                            name="champrecherche"
                                            id="champrecherche"
                                            title={i18n.PLACEHOLDER}
                                            placeholder={i18n.PLACEHOLDER}
                                            value={this.state.inputValue}
                                            onChange={this.onChangeInput}
                                            onKeyDown={this.onInputEnter}
                                            onFocus={this.onInputFocus}
                                            onClick={this.onInputFocus}
                                        />
                                        {matches && (
                                            <label
                                                className="champ-recherche-label hidden-mobonly"
                                                htmlFor="champrecherche">
                                                <SvgComponent
                                                    src={`${STATIC_ROOT}/picto-rechercher.svg`}
                                                    width="18"
                                                    height="18"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                />
                                            </label>
                                        )}
                                    </React.Fragment>
                                )}
                            </MediaQuery>
                        </div>

                        <div className="show-mobonly">
                            <ButtonComponent
                                text={i18n.SEARCH}
                                color="main"
                                cssClass="w100"
                                onClick={this.onSearchClick}
                            />
                        </div>
                        {this.state.showList && (
                            <ul
                                id="recherche-resultats"
                                role="listbox"
                                onMouseDown={this.onResultsMouseDown}
                            >
                                {results.length > 0
                                    ? results.map(r => (
                                          <ResultComponent
                                              key={
                                                  isBilletterie(r)
                                                      ? `${r.id}-${r.venueId}-billetterie`
                                                      : hasEbonCashback(r)
                                                      ? `${r.id}-ebon`
                                                      : r.id
                                              }
                                              partner={r}
                                              onClick={this.closeList(r)}
                                              onFocus={this.onResultFocus}
                                              onBlur={this.onResultBlur}
                                          />
                                      ))
                                    : this.state.inputValue && (
                                          <li className="resultat">
                                              <span className="resultat-conteneur flex-container-h flex-center-alt">
                                                  <div>{i18n.NO_MERCHANT}</div>
                                              </span>
                                          </li>
                                      )}
                            </ul>
                        )}
                        <div aria-live="polite" aria-atomic="true" className="sr-only">
                            {this.state.showList && results.length > 0 && (
                                <p>{i18n.SUGGESTIONS_COUNT.replace('{0}', results.length)}</p>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        );
    }
}

RechercheComponent.propTypes = {
    i18n: PropTypes.object.isRequired,
    onSearchFocus: PropTypes.func,
    onSearchBlur: PropTypes.func
};

// Which props do we want to inject, given the global state?
const mapStateToProps = state => ({
    i18n: state.i18n.feature.header.SEARCH,
    isMenuMobileSearchOpen: isSpecificMobileNavOpen(state, RECHERCHE_VERSION_MENU_MOBILE_SLIDE),
    menuMobileVersion: getMenuVersion(state),
    partners: state.partner.partners,
    ebonPartners: state.ebon.ebonPartners,
    billetterieVenues: state.billetterie.venues,
    pathname: state.routing.location.pathname,
    i18nAccessibility: state.i18n.feature.header.ACCESSIBILITY
});

const mapDispatchToProps = {
    searchInput,
    searchClear,
    toggleMenuMobileSlide
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RechercheComponent));



// WEBPACK FOOTER //
// ./src/components/header/search/SearchComponent.js